import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

import { UpdateEndCustomerCommand, useProductCenterApi } from 'api';
import { endCustomerKeys } from 'api/queries';
import { useValidationErrorsSnackbar } from 'library/hooks/useValidationErrorsSnackbar';

import { mapEndCustomerToUpdateRequestData } from './Mappers';
import { CustomerFormSubmitType, EndCustomerWithLanguageDto } from './types';

export const useUpdateCustomerFormSubmit = ({
	onSuccess,
	onFailure,
}: CustomerFormSubmitType) => {
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation(['customer', 'common']);
	const queryClient = useQueryClient();
	const { enqueueValidationSnackbar } = useValidationErrorsSnackbar();
	const { api } = useProductCenterApi();

	const customerMutation = useMutation<
		EndCustomerWithLanguageDto,
		unknown,
		UpdateEndCustomerCommand
	>({
		mutationFn: async (newValues) =>
			(await api.updateEndCustomer(newValues)).data,
		onSuccess: async (_, { id }) => {
			await queryClient.invalidateQueries({
				queryKey: endCustomerKeys.customer(id),
			});
		},
	});

	const handleFormSubmit = async (data: EndCustomerWithLanguageDto) => {
		try {
			onSuccess(
				await customerMutation.mutateAsync(
					mapEndCustomerToUpdateRequestData(data)
				)
			);
		} catch (error) {
			enqueueValidationSnackbar(error as Error);

			typeof onFailure === 'function' && onFailure();
		}
	};

	const handleFormInvalid = () => {
		enqueueSnackbar(t('common:validation-error-message'), {
			variant: 'error',
		});
	};

	return {
		handleFormSubmit,
		handleFormInvalid,
		customerMutation,
	};
};
